import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO, breadcrumbSchema, orgSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { HelixMotif } from "@/components/brand/MoleculeBg";

export default function About() {
  useSEO({
    title: "About Renova Peptide Co. | Research-Focused Compounds",
    description:
      "Renova Peptide Co. provides carefully presented research compounds for qualified laboratory and analytical applications, built around precision, clarity, and premium presentation.",
    path: "/about",
    jsonLd: [orgSchema, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])],
  });

  return (
    <>
      <PageHeader
        overline="Our Story"
        title="About Renova"
        intro="A research-focused company built around precision, clarity, and professional presentation."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
      />

      <section className="container-rnv py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E2A44] leading-tight">
              Precision, presented with care.
            </h2>
            <div className="mt-6 space-y-5 text-[#262E3D] leading-relaxed">
              <p>
                Renova Peptide Co. exists to make research materials easy to understand and
                confident to handle. We focus on the details that matter to researchers: clear
                identification, organized information, and consistent, professional presentation.
              </p>
              <p>
                Our catalogue is presented strictly for laboratory research and analytical use. We
                do not make medical, treatment, or human-use claims, and we do not overstate what a
                material is or does. When supporting documentation is supplied for a specific lot,
                we publish it plainly.
              </p>
              <p>
                The result is a shopping experience that feels understated and premium — one that
                respects the seriousness of the work our customers do.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="btn-navy">Explore the Catalogue <ArrowRight size={16} /></Link>
              <Link to="/quality-standards" className="btn-gold">Our Standards</Link>
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="relative bg-[#1E2A44] rounded-sm p-10 overflow-hidden min-h-[360px] flex items-center justify-center">
                <HelixMotif className="h-[80%] w-24" color="#C8A96A" opacity={0.6} />
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="overline !text-[#D8BE87]">Est. Research Supply</p>
                  <p className="font-serif text-2xl text-[#F7F4EF] mt-2">Renova Peptide Co.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
